'use client';

import { useTranslations } from 'next-intl';
import { FileText, Pencil, Settings2, Trash2 } from 'lucide-react';
import { Link } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import { TemplateFieldsInfo } from './template-fields-info';
import type { OcrTemplate } from '../types';

const STATUS_STYLES: Record<string, string> = {
  active:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

interface OcrTemplateCompactRowProps {
  template: OcrTemplate;
  onEdit: (template: OcrTemplate) => void;
  onDelete: (template: OcrTemplate) => void;
}

export function OcrTemplateCompactRow({
  template,
  onEdit,
  onDelete
}: OcrTemplateCompactRowProps) {
  const t = useTranslations('ocrTemplates');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md'>
        <FileText className='text-primary h-3.5 w-3.5' />
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-1.5'>
          <p className='truncate font-medium'>{template.name}</p>
          <TemplateFieldsInfo templateId={template.id} />
        </div>
      </div>

      <span className='text-muted-foreground hidden truncate text-sm sm:inline-block'>
        {template.dynamic_form?.name ?? '-'}
      </span>

      <div className='flex shrink-0 items-center gap-2'>
        <Badge variant='outline' className='gap-1' title={t('customFields')}>
          {template.custom_fields_count ?? 0}
        </Badge>
        <Badge variant='outline' className='gap-1' title={t('fieldRules')}>
          {template.field_rules_count ?? 0}
        </Badge>
        <Badge
          variant='outline'
          className={cn(
            'text-xs uppercase',
            STATUS_STYLES[template.is_active ? 'active' : 'inactive']
          )}
        >
          {template.is_active ? t('statusActive') : t('statusInactive')}
        </Badge>
      </div>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['ocr_templates.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(template)}
            title={t('edit')}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['ocr_templates.edit']}>
          <Button
            asChild
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            title={t('configure')}
          >
            <Link href={`/dashboard/ocr-templates/${template.id}/configure`}>
              <Settings2 className='h-3.5 w-3.5' />
            </Link>
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['ocr_templates.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onDelete(template)}
            title={t('delete')}
          >
            <Trash2 className='text-destructive h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
